import React, { ComponentType, CSSProperties, FunctionComponent, useMemo } from 'react';

import { isFunction, isObject } from 'lodash';
import { Button } from 'react-bootstrap';
import { withTranslation, WithTranslation } from 'react-i18next';
import ReactSelect, {
    components,
    ControlProps,
    MenuListComponentProps,
    OptionProps,
    OptionTypeBase,
    PlaceholderProps,
    Props,
} from 'react-select';
import ReactAsyncSelect from 'react-select/async';
import ReactAsyncCreatable from 'react-select/async-creatable';
import ReactCreatableSelect from 'react-select/creatable';
import { FixedSizeList as List } from 'react-window';

import { ButtonVariant } from 'core/util/Bootstrap';
import { Color } from 'core/util/Variables';

// @see React Select https://react-select.com/
// @see Style keys https://react-select.com/styles#style-object

const MenuList = (props: any): JSX.Element => {
    const {
        children,
        clearValue,
        options,
        selectProps: { t },
        setValue,
    } = props;
    const headerStyle = {
        padding: '0 0 0 12px',
    };

    const selectAll = (): void => {
        setValue(options, 'select-option');
    };

    const selectNone = (): void => {
        clearValue();
    };

    return (
        <components.MenuList {...props}>
            <div className="select-header py-2" style={headerStyle}>
                <span className="text-muted pr-2">{t('SELECT')}</span>
                <Button className="px-2 py-0" variant={ButtonVariant.Link} onClick={(): void => selectAll()}>
                    {t('SELECT_ALL')}
                </Button>
                <span className="text-muted">|</span>
                <Button className="px-2 py-0" variant={ButtonVariant.Link} onClick={(): void => selectNone()}>
                    {t('SELECT_NONE')}
                </Button>
            </div>
            {children}
        </components.MenuList>
    );
};

const Option = (props: any): JSX.Element => {
    const { isSelected, label } = props;
    const optionStyle = {
        padding: '0 0 0 12px',
    };

    return (
        <components.Option {...props}>
            <input type="checkbox" checked={!!isSelected} onChange={(): void => undefined} />
            <span style={optionStyle}>{label}</span>
        </components.Option>
    );
};

const ValueContainer: FunctionComponent<any> = ({ children, ...innerProps }) => {
    const {
        selectProps: { maxSelections = 3, t },
    } = innerProps;

    const length = children[0]?.length;
    const childComps = [...children];

    if (length >= maxSelections) {
        childComps[0] = t('N_SELECTED', { length });
    }
    return <components.ValueContainer {...innerProps}>{childComps}</components.ValueContainer>;
};

export const CustomCreateOption = (props: OptionProps<OptionTypeBase>): JSX.Element => {
    const {
        data,
        selectProps: { onCreateClick },
    } = props;
    const optionProps = props;

    if (data.custom) {
        optionProps.innerProps.onClick = onCreateClick;
    }
    return <components.Option {...optionProps} />;
};

const r4eStyles = <P extends object>(Component: ComponentType<P>): FunctionComponent<P & WithTranslation> => {
    /*
     * TODO:
     *  - multi checkboxes + header label?
     */

    return (props: any): JSX.Element => {
        const { isMulti, isInvalid, menuPortalZIndex = 0, styleOverrides, controlHeight } = props;
        let { placeholder, invalidText } = props;
        let componentOptions;

        if (!placeholder) {
            placeholder = isMulti ? props.t('SEARCH') : props.t('SELECT');
        }

        if (!invalidText) {
            invalidText = props.t('VALIDATION_SELECT_REQUIRED');
        }

        const applyStyleOverride = (
            defaultStyles: CSSProperties,
            state: Props,
            styleOverride: CSSProperties,
        ): CSSProperties => {
            if (styleOverride) {
                if (isFunction(styleOverride)) {
                    return styleOverride(defaultStyles, state);
                }

                if (isObject(styleOverride)) {
                    return { ...defaultStyles, ...styleOverride };
                }
            }

            return defaultStyles;
        };

        const styles = {
            clearIndicator: (provided: CSSProperties): any => ({
                ...provided,
                color: Color.Grey2,
                paddingRight: 0,
            }),
            control: (provided: CSSProperties, state: ControlProps<any>): any => {
                const controlStyles: any = {
                    ...provided,
                    backgroundColor: Color.White,
                    borderColor: Color.Grey5,
                    borderRadius: 0,
                    color: Color.Grey6,
                    minHeight: '32px',
                    height: controlHeight,
                    '&:hover': {
                        backgroundColor: Color.White,
                        borderColor: Color.Grey5,
                        boxShadow: 'none',
                        outline: 0,
                    },
                };

                if (state.isDisabled) {
                    controlStyles.backgroundColor = Color.Grey9;
                    controlStyles.color = Color.Grey2;
                    controlStyles.opacity = 1;
                    controlStyles.cursor = 'not-allowed';
                    controlStyles.pointerEvents = 'auto';
                    controlStyles['&:hover'] = {
                        backgroundColor: Color.Grey9,
                    };
                }

                if (state.isFocused) {
                    controlStyles.borderColor = Color.Turquoise3;
                    controlStyles.boxShadow = 'none';
                    controlStyles.outline = 0;
                }

                if (isInvalid) {
                    controlStyles.borderColor = Color.Danger;
                    controlStyles['&:focus'] = {
                        boxShadow: `0 0 0 0.2rem ${Color.Danger}40`,
                    };
                    controlStyles['&:hover'] = {
                        borderColor: Color.Danger,
                    };
                }

                return applyStyleOverride(controlStyles, state, styleOverrides?.control);
            },
            dropdownIndicator: (provided: CSSProperties): any => ({
                ...provided,
                color: Color.Grey2,
            }),
            group: (provided: CSSProperties): any => ({
                ...provided,
                paddingTop: '5px',
                paddingBottom: 0,
            }),
            groupHeading: (provided: CSSProperties): any => ({
                ...provided,
                borderBottom: `1px solid ${Color.Grey4}`,
                color: Color.Grey2,
                fontSize: '13px',
                margin: '0 5px',
                padding: '3px 5px',
            }),
            indicatorsContainer: (provided: CSSProperties, state: Props): any =>
                applyStyleOverride(provided, state, styleOverrides?.indicatorsContainer),
            indicatorSeparator: (provided: CSSProperties, state: Props): any => {
                const indicatorSeparatorStyle = {
                    ...provided,
                    display: 'none',
                };

                return applyStyleOverride(indicatorSeparatorStyle, state, styleOverrides?.indicatorSeparator);
            },
            input: (provided: CSSProperties, state: Props): any =>
                applyStyleOverride(provided, state, styleOverrides?.input),
            menu: (provided: CSSProperties, state: Props): any => {
                const menuStyles = {
                    ...provided,
                    borderRadius: 0,
                    marginTop: '1px',
                    zIndex: 9999,
                };

                return applyStyleOverride(menuStyles, state, styleOverrides?.menu);
            },
            menuPortal: (provided: CSSProperties): any => ({
                ...provided,
                zIndex: menuPortalZIndex,
            }),
            multiValue: (provided: CSSProperties): any => ({
                ...provided,
                color: Color.Black,
            }),
            multiValueLabel: (provided: CSSProperties, state: OptionProps<any>): any => {
                const multiValueLabelStyles = {
                    ...provided,
                    backgroundColor: Color.Grey9,
                    minWidth: '12px',
                };

                return applyStyleOverride(multiValueLabelStyles, state, styleOverrides?.multiValueLabel);
            },
            multiValueRemove: (provided: CSSProperties, state: OptionProps<any>): any => {
                const multiValueRemoveStyles = {
                    ...provided,
                    backgroundColor: Color.Grey9,
                    color: Color.Grey5,
                    '&:hover': {
                        backgroundColor: 'transparent',
                        color: Color.Grey2,
                    },
                    display: state.data.isFixed ? 'none' : '',
                };

                if (state.isDisabled) {
                    multiValueRemoveStyles.pointerEvents = 'none';
                }

                return applyStyleOverride(multiValueRemoveStyles, state, styleOverrides?.multiValueRemove);
            },
            option: (provided: CSSProperties, state: OptionProps<any>): any => {
                const optionStyles: any = {
                    ...provided,
                    position: 'relative',
                    '&:hover': {
                        backgroundColor: Color.Grey10,
                    },
                };

                if (state.isDisabled) {
                    optionStyles.color = Color.Grey2;
                    optionStyles['&:focus'] = {
                        backgroundColor: 'transparent',
                        color: Color.Grey2,
                        cursor: 'not-allowed',
                    };
                    optionStyles['&:hover'] = {
                        backgroundColor: 'transparent',
                        color: Color.Grey2,
                        cursor: 'not-allowed',
                    };
                }

                if (state.isFocused) {
                    optionStyles.backgroundColor = Color.Grey10;
                }

                if (state.isSelected) {
                    optionStyles.backgroundColor = 'initial';
                    optionStyles.color = 'initial';
                    if (!isMulti) {
                        optionStyles['&::after'] = {
                            color: Color.Turquoise3,
                            content: '"\u2714"',
                            fontFamily: 'r4econs',
                            position: 'absolute',
                            top: '8px',
                            right: '10px',
                        };
                    }
                }

                if (state.data.custom) {
                    optionStyles.color = Color.LinkColor;
                }

                return optionStyles;
            },
            placeholder: (provided: CSSProperties, state: PlaceholderProps<any>): any => {
                let placeholderStyles: any = { ...provided, color: Color.Black };

                placeholderStyles = applyStyleOverride(placeholderStyles, state, styleOverrides?.placeholder);

                if (state.isDisabled) {
                    placeholderStyles.color = Color.Grey2;
                }

                if (isMulti) {
                    placeholderStyles.color = Color.Grey2;
                    placeholderStyles['&::before'] = {
                        color: Color.Grey2,
                        content: '"🔍"',
                        fontFamily: 'r4econs',
                        paddingRight: '12px',
                        verticalAlign: 'text-bottom',
                    };
                }

                return placeholderStyles;
            },
            singleValue: (provided: CSSProperties, state: OptionProps<any>): any =>
                applyStyleOverride(provided, state, styleOverrides?.singleValue),
            valueContainer: (provided: CSSProperties, state: Props): any => {
                const valueContainerStyles = {
                    ...provided,
                    padding: '0 8px',
                    color: Color.Black,
                    flexWrap: 'nowrap' as const,
                };

                return applyStyleOverride(valueContainerStyles, state, styleOverrides?.valueContainer);
            },
        };

        const errorFeedbackStyles = {
            color: Color.Danger,
            marginTop: '0.25rem',
            fontSize: '80%',
            width: '100%',
        };

        if (isMulti) {
            componentOptions = { Option, MenuList, ValueContainer };
        } else {
            componentOptions = { ValueContainer };
        }

        return (
            <>
                <Component
                    closeMenuOnSelect={!isMulti}
                    hideSelectedOptions={false}
                    isClearable={false}
                    placeholder={placeholder}
                    styles={styles}
                    components={componentOptions}
                    isOptionDisabled={(option: OptionProps<any>) => !!option?.isDisabled}
                    {...props}
                />
                {isInvalid && <span style={{ ...errorFeedbackStyles }}>{invalidText}</span>}
            </>
        );
    };
};

export interface MenuListProps extends MenuListComponentProps<OptionTypeBase> {
    height: number;
}

export const SelectMenuList: FunctionComponent<MenuListProps> = ({
    children,
    getValue,
    height,
    maxHeight,
    options,
}) => {
    const value = getValue();
    const initialOffset = options.indexOf(value!) * height;

    const childList = React.Children.toArray(children);

    const listHeight = useMemo(() => {
        const listHeight = childList.length * height;

        return maxHeight > listHeight ? listHeight : maxHeight;
    }, [childList, height, maxHeight]);

    return (
        <List
            width="100%"
            height={listHeight}
            itemCount={childList?.length}
            itemSize={height}
            initialScrollOffset={initialOffset}
        >
            {({ index, style }): JSX.Element => <div style={style}>{childList[index]}</div>}
        </List>
    );
};

export const AsyncCreatable = withTranslation('core')(r4eStyles(ReactAsyncCreatable));
export const AsyncSelect = withTranslation('core')(r4eStyles(ReactAsyncSelect));
export const CreatableSelect = withTranslation('core')(r4eStyles(ReactCreatableSelect));
export const Select = withTranslation('core')(r4eStyles(ReactSelect));
